import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// Mock data for the dashboard
const mockData = {
  totalListings: 128,
  activeBrokers: 42,
  totalVolume: 286000000,
  averageCapRate: 5.8,
  recentDeals: [
    {
      id: '1',
      company: 'CBRE Group',
      broker: 'Alex Johnson',
      email: 'alex.johnson@cbre.com',
      dealType: 'Office',
      price: 12500000,
      size: 45000,
      score: 87,
      date: '2023-07-28',
    },
    {
      id: '2',
      company: 'JLL',
      broker: 'Maria Garcia',
      email: 'maria.garcia@jll.com',
      dealType: 'Retail',
      price: 8700000,
      size: 32000,
      score: 92,
      date: '2023-07-27',
    },
    {
      id: '3',
      company: 'Cushman & Wakefield',
      broker: 'James Wilson',
      email: 'james.wilson@cushwake.com',
      dealType: 'Industrial',
      price: 15600000,
      size: 85000,
      score: 68,
      date: '2023-07-25',
    },
    {
      id: '4',
      company: 'Colliers International',
      broker: 'Sarah Chen',
      email: 'sarah.chen@colliers.com',
      dealType: 'Multifamily',
      price: 32400000,
      units: 124,
      score: 76,
      date: '2023-07-24',
    },
    {
      id: '5',
      company: 'Marcus & Millichap',
      broker: 'Robert Taylor',
      email: 'robert.taylor@marcusmillichap.com',
      dealType: 'Retail',
      price: 5400000,
      size: 18500,
      score: 61,
      date: '2023-07-23',
    },
  ],
};

const Dashboard: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [dashboardData, setDashboardData] = useState(mockData);

  useEffect(() => {
    // Simulate API call
    const fetchDashboardData = async () => {
      try {
        // const response = await api.getDashboardData();
        // setDashboardData(response.data);

        // For now, use mock data
        setTimeout(() => {
          setDashboardData(mockData);
          setIsLoading(false);
        }, 1000);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const getScoreColorClass = (score: number) => {
    if (score >= 80) return 'bg-green-100 text-green-800';
    if (score >= 60) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Commercial Real Estate Dashboard</h1>
        <div className="text-sm text-gray-500">Last updated: {new Date().toLocaleDateString()}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-medium text-gray-500">Total Listings</h2>
          <p className="text-3xl font-bold">{dashboardData.totalListings}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-medium text-gray-500">Active Brokers</h2>
          <p className="text-3xl font-bold">{dashboardData.activeBrokers}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-medium text-gray-500">Total Volume</h2>
          <p className="text-2xl font-bold">{formatCurrency(dashboardData.totalVolume)}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-medium text-gray-500">Avg. Cap Rate</h2>
          <p className="text-3xl font-bold">{dashboardData.averageCapRate}%</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden mt-8">
        <div className="px-6 py-5 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Recent Deals & Opportunities</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Broker
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deal Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size/Units
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Score
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {dashboardData.recentDeals.map((deal) => (
                <tr key={deal.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{deal.company}</div>
                    <div className="text-sm text-gray-500">{deal.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{deal.broker}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      {deal.dealType}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(deal.price)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {deal.size ? `${deal.size.toLocaleString()} SF` : `${deal.units} Units`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getScoreColorClass(deal.score)}`}>
                      {deal.score}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg">
            Fetch New Emails
          </button>
          <Link
            to="/email-link"
            className="bg-gray-200 hover:bg-gray-300 text-center py-3 px-4 rounded-lg">
            Manage Email Connection
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
